import { Lang, INTERNAL_ROUTING } from 'shared/constants';
import { Button, Modal } from 'src/components/design-system';
import styles from './admission-generate-report.module.scss';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Select, TextInput } from 'src/components/design-system';
import { formatFullName } from 'shared/utils';
import { Checkbox } from 'antd';
import analyticsService from 'src/helpers/analytics.service';
import { EVENTS } from 'shared/constants';
import React from 'react';

const REPORT_SCHEMA = Yup.object().shape({
  signature: Yup.object().shape({
    name: Yup.string().max(200, 'Too Long!').required('Name is required'),
    email: Yup.string().email('Email must be a valid email').required('Email is required'),
  }),
  recipients: Yup.array()
    .of(Yup.string().email('Email must be a valid email. '))
    .when('sendMeCopy', {
      is: false,
      then: schema => schema.min(1, 'At least one email needs to be chosen').required(),
      otherwise: schema => schema.notRequired(),
    }),
  sendMeCopy: Yup.boolean(),
});

const AdmissionGenerateReportComponent = React.memo(function AdmissionGenerateReport({
  open = false,
  lang = Lang.ADMISSION_GENERATE_REPORT,
  generateChecklistReport = () => {},
  resetChecklist,
  onFinish = () => {
    analyticsService.track(EVENTS.ADMISSION_GENERATE_REPORT.SEND);
  },
  user,
  users,
}) {
  const [isLoading, setLoadingState] = React.useState(false);
  const [reportUrl, setReportUrl] = React.useState('');

  const createReport = React.useCallback(
    async values => {
      setLoadingState(true);

      const { recipients, sendMeCopy, signature } = values;
      const searchParams = new URLSearchParams(window.location.search);
      const checklistId = searchParams.get(INTERNAL_ROUTING.QUERY_PARAMS.CHECKLIST);

      const res = await generateChecklistReport({
        signature,
        recipients: sendMeCopy ? [...recipients, signature.email] : recipients,
        sendByEmail: true,
      });

      if (res.url) {
        setReportUrl(res.url);
      }
      if (sendMeCopy) {
        recipients.push(signature.email);
      }
      setLoadingState(false);
      analyticsService.track(EVENTS.CHECKLISTS.SIGN_AND_SEND, {
        checklistId: checklistId,
        name: formatFullName(user),
        email: signature.email,
        sendMeCopy: sendMeCopy,
        recipients,
      });
    },
    [generateChecklistReport, user],
  );

  const dismiss = React.useCallback(() => {
    // TODO: analytics

    onFinish({});
  }, [onFinish]);

  const close = React.useCallback(() => {
    // TODO: analytics

    resetChecklist();
    onFinish({});
  }, [onFinish, resetChecklist]);

  const recipientsOptions = React.useMemo(() => {
    return users
      .filter(u => u.id !== user.id)
      .map(u => ({
        value: u.email,
        label: formatFullName(u, { withEmail: true }),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [users, user]);

  const {
    handleChange,
    handleSubmit,
    setFieldValue,
    isValidating,
    values,
    errors,
    isValid,
    dirty,
  } = useFormik({
    validationSchema: REPORT_SCHEMA,
    initialValues: {
      signature: {
        name: formatFullName(user),
        email: user.email,
      },
      sendMeCopy: false,
      recipients: [],
    },
    onSubmit: createReport,
  });

  const handleChangeRecipients = value => {
    setFieldValue('recipients', [...new Set(value).values()]);
  };

  const renderFormContent = () => (
    <form onSubmit={handleSubmit}>
      <TextInput
        label={lang.SIGNATURE_NAME}
        name="signature.name"
        value={values.signature.name}
        error={errors.signature?.name}
        onChange={handleChange}
        placeholder={lang.SIGNATURE_NAME_PLACEHOLDER}
        disabled={isLoading}
        required
      />

      <TextInput
        label={lang.SIGNATURE_EMAIL}
        name="signature.email"
        value={values.signature.email}
        error={errors.signature?.email}
        onChange={handleChange}
        placeholder={lang.SIGNATURE_EMAIL_PLACEHOLDER}
        disabled={isLoading}
        required
      />

      <Checkbox
        className={styles.checkbox}
        checked={values.sendMeCopy}
        onChange={handleChange('sendMeCopy')}
        title={lang.SEND_ME_COPY_PLACEHOLDER}
        disabled={isLoading}>
        <label className={styles.label}>{lang.SEND_ME_COPY}</label>
      </Checkbox>

      <Select
        label={lang.RECIPIENTS}
        mode="tags"
        value={values.recipients}
        name="recipients"
        error={errors.recipients}
        onChange={(name, value) => handleChangeRecipients(value)}
        size="large"
        options={recipientsOptions}
        disabled={isLoading}
      />
    </form>
  );

  const renderSuccessContent = () => (
    <div className={styles.success}>
      <h3>{lang.SEND_TO}</h3>
      <h4>{[...values.recipients].join(', ')}</h4>
      <p>
        <a target="_blank" href={reportUrl} rel="noreferrer">
          {lang.CLICK_HERE}
        </a>
        {` ${lang.VIEW_LINK}`}
      </p>
    </div>
  );

  if (reportUrl) {
    return (
      <Modal
        key={lang.TITLE}
        title={lang.TITLE_AFTER_SIGN}
        open={open}
        onCancel={close}
        cancelText={lang.OK}
        footer={null}>
        {renderSuccessContent()}
        <div className={styles.buttons}>
          <Button onClick={close}>{lang.OK}</Button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      key={lang.TITLE}
      title={lang.TITLE}
      open={open}
      onCancel={dismiss}
      onSubmit={handleSubmit}
      cancelText={lang.CANCEL}
      submitText={lang.SEND}
      loading={isLoading}
      submitDisabled={!isValid || isValidating || !dirty}>
      {renderFormContent()}
    </Modal>
  );
});

export { AdmissionGenerateReportComponent };
